import { toast } from 'sonner'
import { useForm } from 'react-hook-form'

import { StorePersistKeys } from 'src/types'
import { useLocales } from 'src/contexts'
import { TIME_IN_MS } from 'src/constants'

import { useManageMaintenanceHistory } from 'src/hooks/useManageMaintenanceHistory'
import { getFormattedTitle } from 'src/helpers/getFromatedTittle'
import { getFormattedDate } from 'src/helpers/getFormattedDate'

import { getForm, getSelectFields } from 'src/pages/ServiceMaintenance/helpers'
import { ServiceType } from 'src/pages/ServiceMaintenance/enums'
import { queryClient } from 'src/features/AppProviders/constants'
import { AlertUpdateMaintenanceWindowProps } from 'src/features/AlertMaintenanceUpdateWindow/types'
import { MaintenanceProps } from 'src/api/Maintenance/types'

export const useAlertMaintenanceUpdateWindow = ({
  itemId,
  toggle,
  odometer,
  serviceCost,
  date,
  address,
  details,
  serviceType,
  currency,
  distanceUnit,
}: Omit<AlertUpdateMaintenanceWindowProps, 'isOpen'>) => {
  const { translations } = useLocales()

  const { updateMaintenanceHistory, isLoadingGeneral } =
    useManageMaintenanceHistory()

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      date: new Date(date.seconds * TIME_IN_MS.SECOND),
      serviceType,
      address,
      odometer: odometer,
      details,
      serviceCost: serviceCost,
    },
  })

  const onUpdate = (): void => {
    const { serviceType, address, details, date, odometer, serviceCost } =
      getValues()

    const updateItem: MaintenanceProps = {
      serviceType: serviceType || ServiceType.OIL_CHANGE,
      address: address.trim() || '',
      odometer: Number(odometer) || 0,
      details: details.trim() || '',
      serviceCost: Number(serviceCost) || 0,
      date: getFormattedDate(date),
      dateOFLastEdit: new Date(),
      currency,
      distanceUnit,
    }

    updateMaintenanceHistory(
      { payload: updateItem, id: itemId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [StorePersistKeys.MAINTENANCE_HISTORY],
          })
          toggle()
          toast(
            translations.maintenance.historyMaintenance.toast.success.title,
            {
              description:
                translations.maintenance.historyMaintenance.toast.success
                  .description,
              action: {
                label:
                  translations.maintenance.historyMaintenance.toast.success
                    .action.label,
                onClick: () => toast.dismiss(),
              },
            },
          )
        },
        onError: error => {
          toast(translations.maintenance.historyMaintenance.toast.error.title, {
            description:
              translations.maintenance.historyMaintenance.toast.error
                .description + error,
            action: {
              label:
                translations.maintenance.historyMaintenance.toast.error.action
                  .label,
              onClick: () => toast.dismiss(),
            },
          })
        },
      },
    )
  }

  const formattedTitle = (name: string, title: string): string => {
    return getFormattedTitle(name, title, currency.toUpperCase(), distanceUnit)
  }

  return {
    title: translations.maintenance.historyMaintenance.alert.update.title,
    cancel: translations.maintenance.historyMaintenance.alert.cancel,
    updateBtn:
      translations.maintenance.historyMaintenance.alert.update.updateBtn,
    fieldsLocale: translations.maintenance.addMaintenance,
    fields: getForm(translations.maintenance),
    formattedTitle,
    handleSubmit,
    control,
    setValue,
    maintenanceSelectOptions: getSelectFields(translations.maintenance),
    errors,
    isLoadingGeneral,
    onUpdate,
  }
}
